import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, LineChart, Line,
} from 'recharts'
import {
  TrendingUp, TrendingDown, Briefcase, Users, FileText,
  Clock, AlertCircle, ArrowUpRight, IndianRupee,
  Plus, GitBranch, Receipt, CheckSquare, ChevronRight,
} from 'lucide-react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'
import { useActivity } from '../context/ActivityContext'
import { getClients } from '../services/clients.service'
import { getInvoices } from '../services/invoices.service'
import { getTasks } from '../services/tasks.service'
import { getExpenses } from '../services/expenses.service'
import { getProjects } from '../services/projects.service'
import type { Client, Invoice, Task, Expense } from '../types'

function formatINR(n: number) {
  if (n >= 10000000) return '₹' + (n / 10000000).toFixed(1) + 'Cr'
  if (n >= 100000)   return '₹' + (n / 100000).toFixed(1) + 'L'
  if (n >= 1000)     return '₹' + (n / 1000).toFixed(0) + 'k'
  return '₹' + n
}

function getLast12Months() {
  const months = []
  const now = new Date()
  for (let i = 11; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
    months.push({ year: d.getFullYear(), month: d.getMonth(), label: d.toLocaleDateString('en-IN', { month: 'short' }) })
  }
  return months
}

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null
  return (
    <div className="rounded-xl px-3 py-2.5 text-xs" style={{ background: '#1a1a1a', border: '1px solid rgba(255,198,0,0.2)', boxShadow: '0 8px 24px rgba(0,0,0,0.5)' }}>
      <p className="font-semibold text-white mb-2">{label}</p>
      {payload.map((p: any, i: number) => (
        <div key={i} className="flex items-center gap-2 mb-0.5">
          <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: p.color }} />
          <span style={{ color: '#a0a0a0' }}>{p.name}:</span>
          <span className="font-semibold text-white">{formatINR(p.value)}</span>
        </div>
      ))}
    </div>
  )
}

function StatCard({ label, value, delta, deltaPositive, icon: Icon, iconColor, onClick }: {
  label: string; value: string; delta: string; deltaPositive: boolean
  icon: React.ElementType; iconColor: string; onClick?: () => void
}) {
  return (
    <div
      className="rounded-xl p-5 transition-all"
      style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.12)', cursor: onClick ? 'pointer' : 'default' }}
      onClick={onClick}
      onMouseEnter={e => { if (onClick) e.currentTarget.style.borderColor = 'rgba(255,198,0,0.3)' }}
      onMouseLeave={e => { if (onClick) e.currentTarget.style.borderColor = 'rgba(255,198,0,0.12)' }}>
      <div className="flex items-start justify-between mb-3">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${iconColor}18` }}>
          <Icon size={17} style={{ color: iconColor }} />
        </div>
        <div className="flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full"
          style={deltaPositive
            ? { background: 'rgba(232,160,0,0.12)', color: '#e8a000' }
            : { background: 'rgba(204,51,0,0.12)', color: '#cc3300' }}>
          {deltaPositive ? <TrendingUp size={10} /> : <TrendingDown size={10} />}
          {delta}
        </div>
      </div>
      <p className="text-2xl font-bold text-white font-syne mb-0.5">{value}</p>
      <p className="text-xs" style={{ color: '#a0a0a0' }}>{label}</p>
    </div>
  )
}

const ACTIVITY_ICONS: Record<string, { icon: React.ElementType; color: string }> = {
  invoice_paid:      { icon: ArrowUpRight, color: '#e8a000' },
  new_client:        { icon: Users,        color: '#ffc600' },
  project_started:   { icon: Briefcase,    color: '#ffc600' },
  project_done:      { icon: Briefcase,    color: '#e8a000' },
  campaign_launched: { icon: TrendingUp,   color: '#ffc600' },
  overdue_alert:     { icon: AlertCircle,  color: '#cc3300' },
}

type ChartTab = 'revenue' | 'profit'

interface ChartPoint { month: string; revenue: number; expenses: number; profit: number }

function LoadingSkeleton() {
  return (
    <div className="p-6 space-y-5 max-w-screen-xl mx-auto animate-pulse">
      <div className="h-8 w-64 rounded-lg" style={{ background: '#1a1a1a' }} />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="rounded-xl p-5 h-24" style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.12)' }} />
        ))}
      </div>
      <div className="rounded-xl h-72" style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.12)' }} />
    </div>
  )
}

export default function Dashboard() {
  const { currentUser } = useAuth()
  const navigate        = useNavigate()
  const [chartTab, setChartTab] = useState<ChartTab>('revenue')
  const { activities }  = useActivity()

  const [clients,  setClients]  = useState<Client[]>([])
  const [invoices, setInvoices] = useState<Invoice[]>([])
  const [tasks,    setTasks]    = useState<Task[]>([])
  const [expenses, setExpenses] = useState<Expense[]>([])
  const [activeProjectCount, setActiveProjectCount] = useState(0)
  const [loading,  setLoading]  = useState(true)

  useEffect(() => {
    Promise.all([
      getClients(),
      getInvoices(),
      getTasks(),
      getExpenses(),
      getProjects(),
    ]).then(([c, inv, t, exp, proj]) => {
      setClients(c)
      setInvoices(inv)
      setTasks(t)
      setExpenses(exp)
      setActiveProjectCount(proj.filter(p => p.status !== 'done').length)
    }).catch(console.error).finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    const refreshAll = () => {
      Promise.all([getClients(), getInvoices(), getTasks(), getExpenses(), getProjects()])
        .then(([c, inv, t, exp, proj]) => {
          setClients(c)
          setInvoices(inv)
          setTasks(t)
          setExpenses(exp)
          setActiveProjectCount(proj.filter(p => p.status !== 'done').length)
        }).catch(console.error)
    }

    const channels = ['clients', 'invoices', 'tasks', 'expenses', 'projects'].map(table =>
      supabase
        .channel(`dashboard-${table}`)
        .on('postgres_changes', { event: '*', schema: 'public', table }, refreshAll)
        .subscribe()
    )

    return () => { channels.forEach(ch => supabase.removeChannel(ch)) }
  }, [])

  if (loading) return <LoadingSkeleton />

  // Computed stats
  const activeClients   = clients.filter(c => c.status === 'active')
  const totalMRR        = activeClients.reduce((s, c) => s + c.mrr, 0)
  const overdueInvoices = invoices.filter(i => i.status === 'overdue')
  const overdueAmount   = overdueInvoices.reduce((s, i) => s + i.total, 0)

  const now = new Date()
  const newClientsThisMonth = clients.filter(c => {
    const d = new Date(c.joinedAt)
    return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth()
  }).length

  const topClients = [...activeClients].sort((a, b) => b.mrr - a.mrr).slice(0, 5)
  const maxMRR     = topClients[0]?.mrr ?? 1

  const urgentTasks = tasks
    .filter(t => t.status !== 'done' && (t.priority === 'urgent' || t.priority === 'high'))
    .slice(0, 4)

  // Revenue chart data — last 12 months
  const last12 = getLast12Months()
  const chartData: ChartPoint[] = last12.map(({ year, month, label }) => {
    const revenue = invoices
      .filter(inv => {
        if (inv.status !== 'paid') return false
        const d = new Date(inv.issuedAt)
        return d.getFullYear() === year && d.getMonth() === month
      })
      .reduce((s, inv) => s + inv.total, 0)

    const exp = expenses
      .filter(e => {
        const d = new Date(e.date)
        return d.getFullYear() === year && d.getMonth() === month
      })
      .reduce((s, e) => s + e.amount, 0)

    return { month: label, revenue, expenses: exp, profit: revenue - exp }
  })

  // Current month revenue & profit for legend
  const currentMonthData = chartData[chartData.length - 1]

  const hour = new Date().getHours()
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening'

  const quickActions = [
    { label: 'New Invoice',  icon: FileText,    path: '/invoices',   color: '#ffc600' },
    { label: 'Add Deal',     icon: GitBranch,   path: '/pipeline',   color: '#e8a000' },
    { label: 'Add Client',   icon: Users,       path: '/clients',    color: '#ffc600' },
    { label: 'Add Expense',  icon: Receipt,     path: '/expenses',   color: '#ff8c00' },
    { label: 'New Task',     icon: CheckSquare, path: '/tasks',      color: '#ffc600' },
  ]

  return (
    <div className="p-6 space-y-5 max-w-screen-xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-syne text-white">
            {greeting}, {currentUser?.name?.split(' ')[0] ?? 'there'} 👋
          </h1>
          <p className="text-sm mt-0.5" style={{ color: '#a0a0a0' }}>
            Here's what's happening at Clickbait today.
          </p>
        </div>
        <div className="hidden sm:flex items-center gap-2 text-xs px-3 py-1.5 rounded-full"
          style={{ background: 'rgba(255,198,0,0.08)', border: '1px solid rgba(255,198,0,0.2)', color: '#ffc600' }}>
          <Clock size={12} /> {new Date().toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {quickActions.map(({ label, icon: Icon, path, color }) => (
          <button key={label}
            onClick={() => navigate(path, { state: { openAdd: true } })}
            className="flex items-center gap-2.5 px-4 py-3 rounded-xl text-sm font-medium transition-all text-left"
            style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.12)', color: '#a0a0a0' }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(255,198,0,0.3)'; e.currentTarget.style.color = '#fff' }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,198,0,0.12)'; e.currentTarget.style.color = '#a0a0a0' }}>
            <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${color}18` }}>
              <Icon size={14} style={{ color }} />
            </div>
            <span>{label}</span>
            <Plus size={12} className="ml-auto flex-shrink-0" style={{ color: '#555' }} />
          </button>
        ))}
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Total MRR" value={formatINR(totalMRR)} delta={`+${newClientsThisMonth} clients`} deltaPositive={true} icon={IndianRupee} iconColor="#ffc600" onClick={() => navigate('/clients')} />
        <StatCard label="Active Clients" value={`${activeClients.length}`} delta={`+${newClientsThisMonth} this month`} deltaPositive={true} icon={Users} iconColor="#e8a000" onClick={() => navigate('/clients')} />
        <StatCard label="Active Projects" value={`${activeProjectCount}`} delta="in progress" deltaPositive={true} icon={Briefcase} iconColor="#ffc600" onClick={() => navigate('/projects')} />
        <StatCard label="Overdue Invoices" value={formatINR(overdueAmount)} delta={`${overdueInvoices.length} invoices`} deltaPositive={false} icon={FileText} iconColor="#cc3300" onClick={() => navigate('/invoices')} />
      </div>

      {/* Revenue / Profit Chart */}
      <div className="rounded-xl p-5" style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.12)' }}>
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="text-sm font-semibold text-white">Financial Overview</h2>
            <p className="text-xs mt-0.5" style={{ color: '#a0a0a0' }}>Last 12 months</p>
          </div>
          <div className="flex gap-1 p-1 rounded-lg" style={{ background: '#1a1a1a' }}>
            {(['revenue', 'profit'] as ChartTab[]).map(tab => (
              <button key={tab} onClick={() => setChartTab(tab)}
                className="px-3 py-1 rounded-md text-xs font-medium transition-all capitalize"
                style={chartTab === tab
                  ? { background: '#ffc600', color: '#000' }
                  : { background: 'transparent', color: '#555' }}>
                {tab}
              </button>
            ))}
          </div>
        </div>

        <ResponsiveContainer width="100%" height={220}>
          {chartTab === 'revenue' ? (
            <AreaChart data={chartData} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#ffc600" stopOpacity={0.18} />
                  <stop offset="95%" stopColor="#ffc600" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="expGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#cc3300" stopOpacity={0.15} />
                  <stop offset="95%" stopColor="#cc3300" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,198,0,0.06)" />
              <XAxis dataKey="month" tick={{ fill: '#555', fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: '#555', fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={v => formatINR(v)} />
              <Tooltip content={<CustomTooltip />} />
              <Area type="monotone" dataKey="expenses" name="Expenses" stroke="#cc3300" strokeWidth={1.5} fill="url(#expGrad)" strokeDasharray="4 2" />
              <Area type="monotone" dataKey="revenue"  name="Revenue"  stroke="#ffc600" strokeWidth={2}   fill="url(#revGrad)" />
            </AreaChart>
          ) : (
            <LineChart data={chartData} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="profGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#32c864" stopOpacity={0.18} />
                  <stop offset="95%" stopColor="#32c864" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,198,0,0.06)" />
              <XAxis dataKey="month" tick={{ fill: '#555', fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: '#555', fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={v => formatINR(v)} />
              <Tooltip content={<CustomTooltip />} />
              <Line type="monotone" dataKey="profit" name="Profit" stroke="#32c864" strokeWidth={2.5} dot={false} />
            </LineChart>
          )}
        </ResponsiveContainer>

        {/* Chart legend */}
        <div className="flex items-center gap-5 mt-3">
          {chartTab === 'revenue' ? (
            <>
              <span className="flex items-center gap-1.5 text-xs" style={{ color: '#a0a0a0' }}>
                <span className="w-3 h-0.5 rounded-full inline-block" style={{ background: '#ffc600' }} /> Revenue
              </span>
              <span className="flex items-center gap-1.5 text-xs" style={{ color: '#a0a0a0' }}>
                <span className="w-3 h-0.5 rounded-full inline-block" style={{ background: '#cc3300' }} /> Expenses
              </span>
              <span className="ml-auto text-xs font-semibold" style={{ color: '#ffc600' }}>
                {currentMonthData?.month} Revenue: {formatINR(currentMonthData?.revenue ?? 0)}
              </span>
            </>
          ) : (
            <>
              <span className="flex items-center gap-1.5 text-xs" style={{ color: '#a0a0a0' }}>
                <span className="w-3 h-0.5 rounded-full inline-block" style={{ background: '#32c864' }} /> Net Profit
              </span>
              <span className="ml-auto text-xs font-semibold" style={{ color: '#32c864' }}>
                {currentMonthData?.month} Profit: {formatINR(currentMonthData?.profit ?? 0)}
              </span>
            </>
          )}
        </div>
      </div>

      {/* Bottom row */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">

        {/* Activity */}
        <div className="lg:col-span-4 rounded-xl p-5" style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.12)' }}>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-white">Recent Activity</h2>
            {activities.length > 0 && (
              <span className="text-xs px-1.5 py-0.5 rounded font-semibold"
                style={{ background: 'rgba(255,198,0,0.12)', color: '#ffc600' }}>
                {activities.length} new
              </span>
            )}
          </div>
          <div className="space-y-3">
            {activities.slice(0, 6).map(item => (
              <div key={item.id} className="flex items-start gap-3">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 text-sm"
                  style={{ background: 'rgba(255,198,0,0.12)' }}>
                  {item.emoji}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-white leading-snug">{item.description}</p>
                  <p className="text-xs mt-0.5 truncate" style={{ color: '#555' }}>{item.detail}</p>
                </div>
                <span className="text-xs flex-shrink-0 mt-0.5" style={{ color: '#ffc600' }}>{item.timeAgo}</span>
              </div>
            ))}
            {activities.length === 0 && (
              <p className="text-xs" style={{ color: '#555' }}>No recent activity.</p>
            )}
          </div>
        </div>

        {/* Top Clients */}
        <div className="lg:col-span-4 rounded-xl p-5" style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.12)' }}>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-white">Top Clients by MRR</h2>
            <button onClick={() => navigate('/clients')}
              className="text-xs flex items-center gap-1 transition-colors"
              style={{ color: '#555' }}
              onMouseEnter={e => (e.currentTarget.style.color = '#ffc600')}
              onMouseLeave={e => (e.currentTarget.style.color = '#555')}>
              View all <ChevronRight size={11} />
            </button>
          </div>
          <div className="space-y-3.5">
            {topClients.length === 0 ? (
              <p className="text-xs" style={{ color: '#555' }}>No active clients yet.</p>
            ) : topClients.map(client => {
              const pct = (client.mrr / maxMRR) * 100
              return (
                <div key={client.id}>
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                        style={{ background: '#ffc600', color: '#000', fontSize: '10px' }}>
                        {client.name[0]}
                      </div>
                      <span className="text-sm text-white truncate max-w-[120px]">{client.name}</span>
                    </div>
                    <span className="text-sm font-semibold text-white flex-shrink-0">{formatINR(client.mrr)}</span>
                  </div>
                  <div className="h-1 rounded-full" style={{ background: '#1a1a1a' }}>
                    <div className="h-full rounded-full" style={{ width: `${pct}%`, background: '#ffc600', transition: 'width 0.6s ease' }} />
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/* Urgent Tasks + Overdue Invoices */}
        <div className="lg:col-span-4 space-y-4">

          {/* Overdue invoices */}
          {overdueInvoices.length > 0 && (
            <div className="rounded-xl p-4" style={{ background: '#0f0f0f', border: '1px solid rgba(204,51,0,0.2)' }}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <AlertCircle size={14} style={{ color: '#cc3300' }} />
                  <h2 className="text-sm font-semibold text-white">Overdue Invoices</h2>
                </div>
                <button onClick={() => navigate('/invoices')}
                  className="text-xs" style={{ color: '#cc3300' }}>
                  View all →
                </button>
              </div>
              <div className="space-y-2">
                {overdueInvoices.slice(0, 3).map(inv => (
                  <div key={inv.id}
                    className="flex items-center justify-between py-1.5 border-b last:border-0 cursor-pointer rounded transition-colors px-1 -mx-1"
                    style={{ borderColor: 'rgba(204,51,0,0.1)' }}
                    onClick={() => navigate('/invoices', { state: { viewInvoice: inv.id } })}
                    onMouseEnter={e => (e.currentTarget.style.background = 'rgba(204,51,0,0.06)')}
                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-white truncate">{inv.clientName}</p>
                      <p className="text-xs" style={{ color: '#555' }}>{inv.invoiceNumber}</p>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
                      <span className="text-xs font-semibold" style={{ color: '#cc3300' }}>{formatINR(inv.total)}</span>
                      <ChevronRight size={11} style={{ color: '#555' }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Urgent tasks */}
          <div className="rounded-xl p-4" style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.12)' }}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <CheckSquare size={14} style={{ color: '#ffc600' }} />
                <h2 className="text-sm font-semibold text-white">Priority Tasks</h2>
              </div>
              <button onClick={() => navigate('/tasks')}
                className="text-xs" style={{ color: '#555' }}
                onMouseEnter={e => (e.currentTarget.style.color = '#ffc600')}
                onMouseLeave={e => (e.currentTarget.style.color = '#555')}>
                View all →
              </button>
            </div>
            {urgentTasks.length === 0 ? (
              <p className="text-xs" style={{ color: '#555' }}>No urgent tasks 🎉</p>
            ) : (
              <div className="space-y-2">
                {urgentTasks.map(task => {
                  const isUrgent = task.priority === 'urgent'
                  return (
                    <div key={task.id}
                      className="flex items-start gap-2.5 py-1.5 border-b last:border-0 cursor-pointer rounded transition-colors px-1 -mx-1"
                      style={{ borderColor: 'rgba(255,198,0,0.06)' }}
                      onClick={() => navigate('/tasks', { state: { viewTask: task.id } })}
                      onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,198,0,0.04)')}
                      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                      <span className="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0"
                        style={{ background: isUrgent ? '#cc3300' : '#ff8c00' }} />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-white truncate">{task.title}</p>
                        <p className="text-xs mt-0.5" style={{ color: '#555' }}>
                          {task.assignee} · due {new Date(task.dueDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                        </p>
                      </div>
                      <span className="text-xs px-1.5 py-0.5 rounded flex-shrink-0"
                        style={isUrgent
                          ? { background: 'rgba(204,51,0,0.12)', color: '#cc3300' }
                          : { background: 'rgba(255,140,0,0.12)', color: '#ff8c00' }}>
                        {task.priority}
                      </span>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>

      </div>
    </div>
  )
}
